// ═══════════════════════════════════════════════════════════════════════════
// 🔒 PROTEÇÃO: CONSULTA PÚBLICA DE RITs
// Rate Limiting + Logging + IP Blocking + Detecção de Brute Force
// ═══════════════════════════════════════════════════════════════════════════

import { addDoc, collection, serverTimestamp, getDoc, doc, deleteDoc, setDoc } from "firebase/firestore";

const rateLimitStore = new Map();
const RATE_LIMIT_MAX = 5;
const RATE_LIMIT_WINDOW = 5 * 60 * 1000;
const IP_BLOCK_DURATION = 30 * 60 * 1000;
const IP_BLOCK_THRESHOLD = 20;

// ─────────────────────────────────────────────────────────────────────────────
// 1️⃣ RATE LIMITING (Máx 5 tentativas em 5 minutos)
// ─────────────────────────────────────────────────────────────────────────────

export const checkRateLimit = (ip) => {
  const agora = Date.now();

  if (!rateLimitStore.has(ip)) {
    rateLimitStore.set(ip, {
      attempts: [],
      blockedUntil: null,
      totalAttempts: 0,
      lastActivity: agora
    });
  }

  const data = rateLimitStore.get(ip);
  data.lastActivity = agora;

  // Limpar IPs antigos (inativos por > 24h)
  if (rateLimitStore.size > 1000) {
    const cutoffTime = agora - (24 * 60 * 60 * 1000);
    for (const [key, val] of rateLimitStore.entries()) {
      if (val.lastActivity < cutoffTime) {
        rateLimitStore.delete(key);
      }
    }
  }

  // Se IP está bloqueado, verificar duração
  if (data.blockedUntil && agora < data.blockedUntil) {
    const tempoRestante = Math.ceil((data.blockedUntil - agora) / 1000);
    return {
      permitido: false,
      motivo: `IP bloqueado por atividade suspeita. Tente novamente em ${tempoRestante}s`,
      bloqueado: true
    };
  }

  // Limpar tentativas antigas (fora da janela de 5 min)
  data.attempts = data.attempts.filter(t => agora - t < RATE_LIMIT_WINDOW);

  // Verificar limite de tentativas
  if (data.attempts.length >= RATE_LIMIT_MAX) {
    return {
      permitido: false,
      motivo: `Limite de 5 tentativas em 5 minutos atingido. Aguarde.`,
      bloqueado: false
    };
  }

  // Registrar tentativa
  data.attempts.push(agora);
  data.totalAttempts++;

  // Se atingiu limite total, bloquear
  if (data.totalAttempts >= IP_BLOCK_THRESHOLD) {
    data.blockedUntil = agora + IP_BLOCK_DURATION;
    return {
      permitido: false,
      motivo: `IP bloqueado por múltiplas tentativas suspeitas.`,
      bloqueado: true
    };
  }

  return {
    permitido: true,
    tentativasRestantes: RATE_LIMIT_MAX - data.attempts.length
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// 2️⃣ LOGGING DE TENTATIVAS (Registrar no Firestore)
// ─────────────────────────────────────────────────────────────────────────────

export const registrarTentativa = async (db, ip, numero, sucesso, motivo = "") => {
  try {
    await addDoc(collection(db, "consultas_publicas_log"), {
      ip: ip,
      numero_rit: numero,
      sucesso: sucesso,
      motivo: motivo,
      user_agent: navigator.userAgent,
      timestamp: serverTimestamp(),
      createdAt: new Date()
    });
  } catch (e) {
    // Falha silenciosa: não expor detalhes no console do usuário final.
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 3️⃣ VERIFICAR IP BLOQUEADO (Consulta Firestore)
// ─────────────────────────────────────────────────────────────────────────────

export const verificarIPBloqueado = async (db, ip) => {
  try {
    const snap = await getDoc(doc(db, "ips_bloqueados", ip));

    if (snap.exists()) {
      const data = snap.data();

      // Se bloqueio expirou, remover
      if (data.bloqueadoAte && new Date() > data.bloqueadoAte.toDate()) {
        await deleteDoc(doc(db, "ips_bloqueados", ip));
        return false;
      }

      return true;
    }

    return false;
  } catch (e) {
    return true;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 4️⃣ BLOQUEAR IP (Escrever no Firestore)
// ─────────────────────────────────────────────────────────────────────────────

export const bloquearIP = async (db, ip, motivo) => {
  try {
    await setDoc(doc(db, "ips_bloqueados", ip), {
      ip: ip,
      motivo: motivo,
      bloqueadoAte: new Date(Date.now() + IP_BLOCK_DURATION),
      registradoEm: serverTimestamp(),
      createdAt: new Date()
    });
  } catch (e) {
    // Falha silenciosa: não expor detalhes no console do usuário final.
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 5️⃣ OBTER IP DO USUÁRIO (API externa)
// ─────────────────────────────────────────────────────────────────────────────

// Identificador de sessão usado quando o IP real não pode ser obtido (ex: rede
// institucional bloqueando api.ipify.org). Cada sessão recebe um valor único
// em vez de um literal fixo — um literal compartilhado faria um bloqueio
// causado por um único usuário afetar todos os demais que também caíssem no
// fallback.
const obterIdentificadorSessao = () => {
  const KEY = "sisrit_consulta_session_id";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `sess_${typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
};

export const obterIPUsuario = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    if (!data?.ip) throw new Error("IP ausente na resposta da api.ipify.org");
    return data.ip;
  } catch (e) {
    return obterIdentificadorSessao();
  }
};
